import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@evaneos/b2b-ui';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../shared/analytics';
import { AllTranslationKeys } from '../../../shared/intl/types';
import {
    AgencyCapacityReminder,
    AgencyCapacityReminderType,
} from '../domain/collectCapacityReminder/CollectCapacityReminderConfigurator';
import { addSnooze } from '../domain/collectCapacityReminder/CollectCapacityReminderSnoozeHandler';
import { logModalOpened, logModalChoice, logModalClosed } from '../logging';
import { useGetLoggedAgent } from '../../agent/hooks';

interface Props {
    reminder: AgencyCapacityReminder | null;
}

enum Choice {
    NOT_CONCERNED = 'not_concerned',
    REMIND_ME_LATER = 'remind_me_later',
    CONFIRM_CAPACITY = 'confirm_capacity',
}

export const AgencyCapacityReminderModal = ({ reminder }: Props) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const agent = useGetLoggedAgent();
    const [open, setOpen] = useState(false);
    const [relatedMonth, setRelatedMonth] = useState('');

    const months: { [key: string]: AllTranslationKeys } = {
        january: 'agent_settings.agency_capacity.month.january' as AllTranslationKeys,
        february: 'agent_settings.agency_capacity.month.february' as AllTranslationKeys,
        march: 'agent_settings.agency_capacity.month.march' as AllTranslationKeys,
        april: 'agent_settings.agency_capacity.month.april' as AllTranslationKeys,
        may: 'agent_settings.agency_capacity.month.may' as AllTranslationKeys,
        june: 'agent_settings.agency_capacity.month.june' as AllTranslationKeys,
        july: 'agent_settings.agency_capacity.month.july' as AllTranslationKeys,
        august: 'agent_settings.agency_capacity.month.august' as AllTranslationKeys,
        september: 'agent_settings.agency_capacity.month.september' as AllTranslationKeys,
        october: 'agent_settings.agency_capacity.month.october' as AllTranslationKeys,
        november: 'agent_settings.agency_capacity.month.november' as AllTranslationKeys,
        december: 'agent_settings.agency_capacity.month.december' as AllTranslationKeys,
    };

    useEffect(() => {
        if (
            reminder &&
            reminder.type === AgencyCapacityReminderType.CapacityNeedToBeFilled &&
            reminder.remindAt < new Date()
        ) {
            logModalOpened(
                'AgencyCapacityReminderModal',
                {
                    reminderType: reminder.type,
                    remindAt: reminder.remindAt,
                    relatedMonth: reminder.relatedMonth,
                },
                agent,
            );
            setOpen(true);
            setRelatedMonth(
                format(
                    new Date(new Date().getFullYear(), reminder.relatedMonth, 1),
                    'MMMM',
                ).toLowerCase(),
            );
        }
    }, [reminder, agent]);

    const handleChoice = (choice: Choice) => {
        logModalChoice(
            'AgencyCapacityReminderModal',
            {
                choice,
                snoozeDays: choice === Choice.NOT_CONCERNED ? 365 : 8,
            },
            agent,
        );

        trackEvent((ampli, defaultProperties) => {
            ampli.collectCapacityReminderChoiceClicked({
                ...defaultProperties,
                collect_capacity_reminder_choice: choice,
            });
        });

        if (choice === Choice.NOT_CONCERNED) {
            addSnooze(AgencyCapacityReminderType.CapacityNeedToBeFilled, 365);
        } else {
            addSnooze(AgencyCapacityReminderType.CapacityNeedToBeFilled, 8);
        }

        setOpen(false);
    };

    const handleClose = () => {
        logModalClosed(
            'AgencyCapacityReminderModal',
            {
                action: 'dismissed',
                snoozeDays: 8,
            },
            agent,
        );
        addSnooze(AgencyCapacityReminderType.CapacityNeedToBeFilled, 8);
        setOpen(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <p
                    style={{
                        fontSize: `2.2rem`,
                        lineHeight: `3rem`,
                    }}
                >
                    <FormattedMessage id="agent_settings.agency_capacity.reminder.capacity_need_to_be_filled.title" />
                </p>
            </DialogTitle>
            <DialogContent>
                <p
                    style={{
                        fontSize: `1,4rem`,
                        lineHeight: `2rem`,
                    }}
                >
                    <FormattedMessage
                        id="agent_settings.agency_capacity.reminder.capacity_need_to_be_filled.text"
                        values={{
                            month:
                                relatedMonth in months
                                    ? formatMessage({
                                          id: months[relatedMonth as keyof typeof months],
                                      })
                                    : '',
                        }}
                    />
                </p>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={() => handleChoice(Choice.NOT_CONCERNED)}>
                    <FormattedMessage id="agent_settings.agency_capacity.reminder.capacity_need_to_be_filled.im_not_concerned" />
                </Button>
                <Button variant="outlined" onClick={() => handleChoice(Choice.REMIND_ME_LATER)}>
                    <FormattedMessage id="agent_settings.agency_capacity.reminder.capacity_need_to_be_filled.remind_me_later" />
                </Button>
                <a href="/agency-manager/capacity">
                    <Button onClick={() => handleChoice(Choice.CONFIRM_CAPACITY)}>
                        <FormattedMessage id="agent_settings.agency_capacity.reminder.capacity_need_to_be_filled.confirm_capacity" />
                    </Button>
                </a>
            </DialogActions>
        </Dialog>
    );
};
